import {
  AfterViewInit,
  OnInit,
  OnDestroy,
  Component,
  ElementRef,
  HostListener,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DataFormat, DataService } from '../../services/data.service';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { continents } from 'projects/marketpro-ui/src/app/regions';
import {
  currentYear,
  constants,
} from 'projects/marketpro-ui/src/app/constants';
import { countries } from 'projects/marketpro-ui/src/app/countries';
import { Utils } from 'marketpro-design-lib';
@Component({
  selector: 'mpro-size-prize',
  templateUrl: './size-of-the-prize.component.html',
  styleUrls: ['./size-of-the-prize.component.scss'],
})
export class SizeOfThePrize implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('topBar', { read: ElementRef })
  public topBar: ElementRef;

  @ViewChild('container', { read: ElementRef })
  public container: ElementRef;

  @ViewChild('stickyHeader', { read: ElementRef })
  public stickyHeader: ElementRef;
  public headerHeight: number;
  public topBarHeight: number;
  private ngUnsubscribe: Subject<void> = new Subject();
  public dataFilters: DataFilters = new DataFilters();
  public isLoading: boolean = false;
  public tableData: any = [];
  public currentYearTableData: any = [];

  constructor(
    private dataService: DataService,
    private dataFiltersService: DataFiltersService,
    private renderer: Renderer2
  ) {}

  public ngOnInit(): void {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .pipe(tap(() => (this.isLoading = true)))
      .subscribe((filters) => (this.dataFilters = filters));
    this.dataService
      .getData()
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(300))
      .subscribe((data) => {
        if (
          data.countryData.current &&
          data.countryData.total &&
          Object.keys(data.countryData.current).length > 0
        ) {
          const filteredDataCurrentYear = Object.entries(
            data.countryData.current
          ).find((keys) => keys[0] === currentYear.toString())![1];
          const filteredData = Object.entries(data.countryData.current).find(
            (keys) => keys[0] === this.dataFilters.year.toString()
          )![1];
          this.currentYearTableData = Object.values(
            filteredDataCurrentYear
          ).map((item) => item);
          this.tableData = Object.values(filteredData).map((item) => item);
          this.calculateData();
        }
      });
    this.isLoading = false;
  }

  public ngAfterViewInit() {
    this.renderer.setStyle(
      this.container.nativeElement,
      'marginTop',
      `calc(1rem)`
    );
    this.getHeights();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:resize')
  onResize() {
    this.getHeights();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.checkIfFixed();
  }

  public getHeights() {
    this.headerHeight =
      this.stickyHeader.nativeElement.getBoundingClientRect().height;
    this.topBarHeight =
      this.topBar.nativeElement.getBoundingClientRect().height;
  }

  public checkIfFixed(): void {
    if (!this.container.nativeElement || !this.stickyHeader.nativeElement) {
      return;
    }

    if (window.scrollY > this.topBarHeight) {
      this.renderer.setStyle(
        this.container.nativeElement,
        'paddingTop',
        `${this.headerHeight}px`
      );
      this.renderer.addClass(this.stickyHeader.nativeElement, 'sticky');
    } else {
      this.reset();
    }
  }

  private reset(): void {
    this.renderer.removeStyle(this.container.nativeElement, 'paddingTop');
    this.renderer.removeClass(this.stickyHeader.nativeElement, 'sticky');
  }

  private handleColorContinent = (itemCode) =>
    constants.legendItems.find((color) => color.code === itemCode);

  private calculateSpendingPPP = (item) =>
    item.spendingCurrentUsd / Utils.getDaysInAYear(this.dataFilters.year);

  private sortItemsBySpendingPPP = (items) =>
    items.sort((a, b) => b.spending2021ppp - a.spending2021ppp);

  private calculateData() {
    const sortedByCurrentYear = this.sortItemsBySpendingPPP(
      this.currentYearTableData
    );
    const sortedBySelectedYear = this.sortItemsBySpendingPPP(this.tableData);

    const connectValueMap = continents.reduce(
      (map, { countries = [], code }) => {
        countries.forEach((item) => map.set(item, code));
        return map;
      },
      new Map()
    );

    const merged = sortedBySelectedYear.map((item, i) => {
      const value = connectValueMap.get(item.code);

      if (value) item.continentCode = value;

      item.continentCode =
        (value || item.code) === 'SA'
          ? 'NA'
          : (value || item.code) === 'MDV' || (value || item.code) === 'MUS'
          ? 'AS'
          : value || item.code;

      return {
        ...item,
        people: item.headCount,
        year: this.dataFilters.year,
        selectedYear: {
          currentYearSpendingPPP: this.calculateSpendingPPP(
            sortedByCurrentYear[i]
          ),
          initialPosition: i,
          finalPosition: sortedByCurrentYear
            .map(({ code }) => code)
            .indexOf(item.code),
          ranking:
            i - sortedByCurrentYear.map(({ code }) => code).indexOf(item.code),
        },
        spending_ppp: this.calculateSpendingPPP(item),
        name: countries.find((country) => item.code === country.code)?.name,
        continent: this.handleColorContinent(item.continentCode)?.text,
        color: this.handleColorContinent(item.continentCode)?.color,
      };
    });

    if (this.dataFilters.regionSelection.regions.size === 1) {
      const countries = [
        ...this.dataFilters.regionSelection.regions.values(),
      ][0].countries;
      this.tableData = merged.filter(({ code }) => countries.includes(code));
    } else if (this.dataFilters.regionSelection.countries.size === 1) {
      const countryId = [
        ...this.dataFilters.regionSelection.countries.values(),
      ][0].code;
      this.tableData = merged.filter(({ code }) => code === countryId);
    } else {
      this.tableData = merged;
    }
  }
}
